import React, { useState, useRef, useEffect } from 'react';
import {
  generateTxnRef, computeKeyTest, formatUSD, formatDate, todayISO, swiftDate,
  buildMT199, buildMT103, buildMT940, buildMT202, buildMT950, buildMT999,
  KTT_EMAIL, KTT_CODE, SECRET_KEY_CONSTANT, COMPANY,
  toUSD, getFXRate, formatFXAmount,
  resolveAccountKeyTest, commitAccountKeyTest
} from '../utils/keyGenerator';

// Steps: 1=FILL, 2=REVIEW, 3=CONFIRMED/SENT
export default function SendKTT({ wallet, addTransaction, setSlip, accounts, preSelectedAccount, onAccountUsed }) {
  const [step, setStep] = useState(1);
  const [sourceAccountIban, setSourceAccountIban] = useState(preSelectedAccount?.iban || '');
  const [mtType, setMtType] = useState("MT199");
  const [form, setForm] = useState({
    beneficiary: "", accountNo: "", receiverBank: "", senderBIC: "",
    receiverBIC: "", receiverKTTCode: "",
    currency: "USD", amount: "", narrative: "", valueDate: todayISO(),
    remittanceInfo: "", openBal: "", txnType: "debit",
  });
  const [alert, setAlert] = useState(null);
  const [sending, setSending] = useState(false);
  const [preview, setPreview] = useState("");
  const set = k => e => setForm(f => ({ ...f, [k]: e.target.value }));

  // Auto-select source account when navigating from Account page
  useEffect(() => {
    if (preSelectedAccount?.iban) {
      setSourceAccountIban(preSelectedAccount.iban);
      if (onAccountUsed) onAccountUsed();
    }
  }, [preSelectedAccount, onAccountUsed]);

  const txnRef = useRef(generateTxnRef("KTT"));
  // Per-account Key Test: first KTT to an account locks the key; repeats reuse it.
  const keyInfo = resolveAccountKeyTest(form.accountNo, form.amount, form.valueDate);
  const keyTest = keyInfo.keyTest;
  const keyLocked = keyInfo.locked;

  const buildPreview = () => {
    const base = { txnRef: txnRef.current, keyTest, ...form };
    if (mtType === "MT199") return buildMT199(base);
    if (mtType === "MT103") return buildMT103(base);
    if (mtType === "MT940") return buildMT940({
      ...base,
      closeBal: (parseFloat(form.openBal || 0) - parseFloat(form.amount || 0)).toFixed(2)
    });
    if (mtType === "MT202") return buildMT202(base);
    if (mtType === "MT950") return buildMT950({
      ...base,
      closeBal: (parseFloat(form.openBal || 0) - parseFloat(form.amount || 0)).toFixed(2)
    });
    if (mtType === "MT999") return buildMT999(base);
    return "";
  };

  useEffect(() => {
    setPreview(buildPreview());
  }, [form, mtType, keyTest]); // eslint-disable-line

  // Validate before going to review
  const handleGoToReview = () => {
    if (!form.beneficiary) { setAlert({ type: "error", msg: "Beneficiary is required." }); return; }
    if (!form.accountNo) { setAlert({ type: "error", msg: "Account Number is required." }); return; }
    if (!form.amount || parseFloat(form.amount) <= 0) { setAlert({ type: "error", msg: "Please enter a valid amount." }); return; }
    // Only MT103 and MT202 require sufficient balance — MT199/MT940/MT950/MT999 are informational
    if (mtType === 'MT103' || mtType === 'MT202') {
      const usdEquiv = toUSD(parseFloat(form.amount), form.currency);
      if (usdEquiv > wallet.balance) { setAlert({ type: "error", msg: `Insufficient balance. ${formatFXAmount(parseFloat(form.amount), form.currency)} = ${formatUSD(usdEquiv)} USD. Available: ${formatUSD(wallet.balance)}` }); return; }
    }
    setAlert(null);
    setStep(2);
  };

  // Go back to edit
  const handleBackToEdit = () => {
    setAlert(null);
    setStep(1);
  };

  // Confirm and send
  const handleConfirmSend = async () => {
    setSending(true);
    const amt = parseFloat(form.amount);
    const txRef = txnRef.current;
    // Lock (or reuse) the per-account Key Test. New account → this amount/date
    // computes the permanent key; repeat account → returns the original key.
    const finalKeyTest = commitAccountKeyTest(form.accountNo, amt, form.valueDate);
    const msgBody = buildPreview();

    // Call backend — includes real email dispatch via SMTP
    let emailSent = false;
    try {
      const backendResp = await fetch('/api/ktt/send', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          amount: amt, currency: form.currency, mtType,
          beneficiary: form.beneficiary, accountNo: form.accountNo,
          receiverBank: form.receiverBank, receiverBIC: form.receiverBIC,
          receiverKTTCode: form.receiverKTTCode, senderBIC: form.senderBIC,
          narrative: form.narrative, valueDate: form.valueDate,
          swiftMessage: msgBody,
          sourceAccountIban: sourceAccountIban || undefined,
        }),
      });
      if (!backendResp.ok) {
        const err = await backendResp.json().catch(() => ({}));
        if (err.error) { setSending(false); setAlert({ type: 'error', msg: err.error }); return; }
      } else {
        const result = await backendResp.json();
        emailSent = result.emailSent;
      }
    } catch (err) {
      console.warn('Backend sync:', err.message);
    }

    // Only MT103 and MT202 debit funds; MT199/MT940/MT950/MT999 are messages only
    const fxRate = getFXRate(form.currency);
    const srcAcct = accounts?.find(a => a.iban === sourceAccountIban);
    const isFundTransfer = mtType === 'MT103' || mtType === 'MT202';
    addTransaction({
      type: "KTT", mt: mtType,
      debitAmount: isFundTransfer ? toUSD(amt, form.currency) : 0,
      description: (form.narrative || `${mtType} ${isFundTransfer ? 'Transfer' : 'Message'}`) + (isFundTransfer && form.currency !== 'USD' ? ` (${formatFXAmount(amt, form.currency)} @ ${fxRate})` : ''),
      counterparty: form.beneficiary, beneficiary: form.beneficiary,
      accountNo: form.accountNo, bankName: form.receiverBank,
      receiverBIC: form.receiverBIC, receiverKTTCode: form.receiverKTTCode,
      currency: form.currency, keyTest: finalKeyTest, valueDate: form.valueDate,
      accountIban: sourceAccountIban || null,
      accountHolder: srcAcct?.holderName || null,
    });

    setSending(false);
    setStep(3);
    const isoType = isoMap[mtType] || mtType;
    setAlert({ type: "success", msg: `${isoType} (${mtType}) sent via KTT. Ref: ${txRef} | Key Test: ${finalKeyTest}${emailSent ? ' | Email dispatched to ' + KTT_EMAIL : ''}` });
    setSlip({
      ...form, txnRef: txRef, method: `${isoType} (${mtType})`, keyTest: finalKeyTest,
      kttEmail: KTT_EMAIL, kttCode: KTT_CODE,
      receiverKTTCode: form.receiverKTTCode, receiverBIC: form.receiverBIC,
    });
  };

  // Reset for new transaction
  const handleNewTransaction = () => {
    txnRef.current = generateTxnRef("KTT");
    setForm({
      beneficiary: "", accountNo: "", receiverBank: "", senderBIC: "",
      receiverBIC: "", receiverKTTCode: "",
      currency: "USD", amount: "", narrative: "", valueDate: todayISO(),
      remittanceInfo: "", openBal: "", txnType: "debit",
    });
    setStep(1);
    setAlert(null);
  };

  // ISO 20022 type mapping
  const isoMap = {
    MT199: "pacs.009",
    MT103: "pacs.008",
    MT940: "camt.053",
    MT202: "pacs.009 (Cover)",
    MT950: "camt.052",
    MT999: "camt.998",
  };

  // ===== REVIEW ROWS =====
  const reviewRows = [
    { label: "ISO 20022 Message Type", value: `${isoMap[mtType]} (${mtType})`, highlight: true },
    { label: "Transaction Reference", value: txnRef.current, mono: true },
    { label: "Key Test Number", value: keyTest, gold: true },
    { label: "Beneficiary", value: form.beneficiary },
    { label: "Account Number", value: form.accountNo },
    { label: "Receiver Bank", value: form.receiverBank || "-" },
    { label: "Receiver BIC/SWIFT", value: form.receiverBIC || "-" },
    { label: "Receiver KTT Code", value: form.receiverKTTCode || "-" },
    { label: "Sender BIC", value: form.senderBIC || "-" },
    { label: "Sender KTT Code", value: KTT_CODE },
    { label: "Currency", value: form.currency },
    { label: "Amount", value: formatUSD(parseFloat(form.amount || 0)), highlight: true },
    { label: "Value Date", value: form.valueDate },
    { label: "Narrative", value: form.narrative || "-" },
    { label: "KTT Email (Dispatch To)", value: KTT_EMAIL },
  ];

  return (
    <div className="gap-16">
      {alert && (
        <div className={`alert alert-${alert.type}`}>
          {alert.type === "success" ? "\u2713" : "\u2717"} {alert.msg}
        </div>
      )}

      {/* STEP INDICATOR */}
      <div style={{
        display: "flex", gap: 0, background: "var(--surface)", border: "1px solid var(--border)",
        borderRadius: "var(--radius-lg)", overflow: "hidden"
      }}>
        {[
          { n: 1, label: "Fill Details" },
          { n: 2, label: "Review & Edit" },
          { n: 3, label: "Confirmed" },
        ].map(s => (
          <div key={s.n} style={{
            flex: 1, padding: "14px 20px", textAlign: "center",
            background: step >= s.n ? (step === s.n ? "var(--gold-soft)" : "var(--surface2)") : "var(--surface)",
            borderRight: s.n < 3 ? "1px solid var(--border)" : "none",
            fontWeight: step === s.n ? 700 : 500,
            color: step >= s.n ? "var(--gold)" : "var(--text3)",
            fontSize: 13,
          }}>
            <span style={{ marginRight: 8, fontWeight: 700 }}>{s.n}</span>{s.label}
          </div>
        ))}
      </div>

      {/* ===== STEP 1: FILL ===== */}
      {step === 1 && (
        <>
          {/* ISO 20022 MESSAGE TYPE SELECTOR */}
          <div className="mt-selector">
            {[
              { id: "MT199", label: "pacs.009",       desc: "FI Credit Transfer (MT199)" },
              { id: "MT103", label: "pacs.008",       desc: "Customer Credit Transfer (MT103)" },
              { id: "MT940", label: "camt.053",       desc: "Bank Statement (MT940)" },
              { id: "MT202", label: "pacs.009 Cover", desc: "FI Cover Payment (MT202)" },
              { id: "MT950", label: "camt.052",       desc: "Statement / Balance Report (MT950)" },
              { id: "MT999", label: "camt.998",       desc: "Free Format Message (MT999)" },
            ].map(m => (
              <button key={m.id} className={`mt-btn ${mtType === m.id ? "active" : ""}`} onClick={() => setMtType(m.id)}>
                <span className="mt-label">{m.label}</span>
                <span className="mt-desc">{m.desc}</span>
              </button>
            ))}
          </div>

          <div className="grid2">
            <div className="gap-16">
              {/* KEY TEST */}
              <div className="kt-box">
                <div className="kt-label" style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                  <span>{keyLocked ? "ACCOUNT KEY TEST NUMBER" : "AUTO-COMPUTED KEY TEST NUMBER"}</span>
                  {form.accountNo ? (
                    <span style={{
                      fontSize: 10, fontWeight: 700, letterSpacing: "0.08em",
                      padding: "2px 8px", borderRadius: 10,
                      background: keyLocked ? "var(--gold-soft)" : "rgba(13,148,136,0.1)",
                      color: keyLocked ? "var(--gold)" : "var(--teal)",
                      border: `1px solid ${keyLocked ? "var(--gold)" : "var(--teal)"}`,
                    }}>
                      {keyLocked ? "🔒 LOCKED · REUSED" : "NEW · LOCKS ON SEND"}
                    </span>
                  ) : null}
                </div>
                <div className="kt-value">{keyTest}</div>
                <div className="kt-formula">
                  {keyLocked
                    ? `Reused from first KTT to account ${form.accountNo} — fixed for all future wires to this account.`
                    : `f(Amount=${form.amount || "0"} + Date=${swiftDate(form.valueDate)} + SecretKey=${SECRET_KEY_CONSTANT}) mod 99999`}
                </div>
              </div>

              {/* TXN REF */}
              <div className="form-group">
                <label className="form-label">Transaction Reference (Auto)</label>
                <input className="form-input" value={txnRef.current} readOnly
                  style={{ color: "var(--gold)", fontWeight: 600, cursor: "default", fontFamily: "'IBM Plex Mono', monospace" }} />
              </div>

              {/* FORM */}
              <div className="card">
                <div className="card-header"><span className="card-title">Payment Fields</span></div>
                <div className="card-body">
                  <div className="gap-16">
                    {[
                      { label: "Beneficiary *", key: "beneficiary", placeholder: "Enter beneficiary full legal name" },
                      { label: "Account Number *", key: "accountNo", placeholder: "Enter account number or IBAN" },
                      { label: "Receiver Bank", key: "receiverBank", placeholder: "Enter receiver bank name" },
                      { label: "Receiver BIC/SWIFT", key: "receiverBIC", placeholder: "Enter BIC/SWIFT code" },
                      { label: "Receiver KTT Code", key: "receiverKTTCode", placeholder: "Enter receiver KTT code" },
                      { label: "Sender BIC (MT103)", key: "senderBIC", placeholder: "Enter sender BIC" },
                    ].map(f => (
                      <div key={f.key} className="form-group">
                        <label className="form-label">{f.label}</label>
                        <input className="form-input" placeholder={f.placeholder} value={form[f.key]} onChange={set(f.key)} />
                      </div>
                    ))}
                    <div className="grid2">
                      <div className="form-group">
                        <label className="form-label">Currency</label>
                        <select className="form-input" value={form.currency} onChange={set("currency")}>
                          <option>USD</option><option>EUR</option><option>GBP</option><option>THB</option>
                        </select>
                      </div>
                      <div className="form-group">
                        <label className="form-label">Amount *</label>
                        <input className="form-input" type="number" placeholder="0.00" min="0.01" step="0.01"
                          value={form.amount} onChange={set("amount")} />
                      </div>
                    </div>
                    <div className="form-group">
                      <label className="form-label">Value Date</label>
                      <input className="form-input" type="date" value={form.valueDate} onChange={set("valueDate")} />
                    </div>
                    {(mtType === "MT940" || mtType === "MT950") && (
                      <div className="form-group">
                        <label className="form-label">Opening Balance ({mtType})</label>
                        <input className="form-input" type="number" placeholder="0.00" value={form.openBal} onChange={set("openBal")} />
                      </div>
                    )}
                    {mtType === "MT999" && (
                      <div className="form-group" style={{ padding: "10px 14px", background: "var(--gold-soft)", borderRadius: "var(--radius)", border: "1px solid var(--gold)", fontSize: 12, color: "var(--gold)" }}>
                        <strong>MT999 — Free Format:</strong> All fields are optional. Only Narrative / Free Text is required. Amount field is for reference only — no funds debited.
                      </div>
                    )}
                    {mtType === "MT202" && (
                      <div className="form-group" style={{ padding: "10px 14px", background: "rgba(15,32,64,0.04)", borderRadius: "var(--radius)", border: "1px solid var(--border)", fontSize: 12, color: "var(--text2)" }}>
                        <strong>MT202 — Cover Payment:</strong> Interbank settlement / nostro funding. Debits wallet balance. Use Receiver BIC for the correspondent bank.
                      </div>
                    )}
                    {mtType === "MT950" && (
                      <div className="form-group" style={{ padding: "10px 14px", background: "rgba(15,32,64,0.04)", borderRadius: "var(--radius)", border: "1px solid var(--border)", fontSize: 12, color: "var(--text2)" }}>
                        <strong>MT950 — Statement Message:</strong> Balance report / intraday statement. Set Opening Balance and the entry amount. No funds debited.
                      </div>
                    )}
                    <div className="form-group">
                      <label className="form-label">Narrative / Remittance Info</label>
                      <textarea className="form-input" value={form.narrative} onChange={set("narrative")} placeholder="Payment details..." />
                    </div>
                    {accounts && accounts.length > 0 && (
                      <div className="form-group">
                        <label className="form-label">Source Account</label>
                        <select className="form-input" value={sourceAccountIban} onChange={e => setSourceAccountIban(e.target.value)}>
                          <option value="">Main Wallet</option>
                          {accounts.filter(a => a.status === 'ACTIVE').map(a => (
                            <option key={a.iban} value={a.iban}>
                              {a.ibanFormatted} — {a.holderName} — {formatUSD(a.balance)}
                            </option>
                          ))}
                        </select>
                      </div>
                    )}
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 8 }}>
                      <span style={{ fontSize: 13, color: "var(--text2)" }}>
                        Available: <strong style={{ color: "var(--teal)" }}>{formatUSD(sourceAccountIban ? (accounts?.find(a => a.iban === sourceAccountIban)?.balance || 0) : wallet.balance)}</strong>
                      </span>
                      <button className="btn btn-gold btn-lg" onClick={handleGoToReview}>
                        Review Before Sending &rarr;
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* SWIFT Preview */}
            <div className="card" style={{ alignSelf: "start", position: "sticky", top: 0 }}>
              <div className="card-header">
                <span className="card-title">ISO 20022 XML Preview</span>
                <button className="btn btn-outline btn-sm" onClick={() => navigator.clipboard.writeText(preview)}>Copy</button>
              </div>
              <div className="card-body">
                <div className="code-block">{preview || "Fill in the fields to preview..."}</div>
              </div>
            </div>
          </div>
        </>
      )}

      {/* ===== STEP 2: REVIEW & EDIT ===== */}
      {step === 2 && (
        <div className="grid2">
          <div className="gap-16">
            <div className="card">
              <div className="card-header">
                <span className="card-title">Review All Details</span>
                <span className="badge badge-pending" style={{ fontSize: 11, padding: "4px 12px" }}>PENDING CONFIRMATION</span>
              </div>
              <div className="card-body">
                <div style={{ fontSize: 13, color: "var(--text2)", marginBottom: 16, lineHeight: 1.7 }}>
                  Please review all details below carefully. You can <strong style={{ color: "var(--gold)" }}>edit any field</strong> before confirming.
                  Once confirmed, the {mtType} message will be dispatched via KTT.
                </div>

                {reviewRows.map((r, i) => (
                  <div key={i} style={{
                    display: "flex", justifyContent: "space-between", alignItems: "center",
                    padding: "10px 0", borderBottom: "1px solid var(--border-light)",
                  }}>
                    <span style={{ fontSize: 12, color: "var(--text3)", fontWeight: 600, letterSpacing: "0.05em", minWidth: 180 }}>
                      {r.label}
                    </span>
                    <span style={{
                      fontSize: r.highlight ? 16 : 14,
                      fontWeight: r.highlight || r.gold ? 700 : 500,
                      color: r.gold ? "var(--gold)" : r.highlight ? "var(--navy)" : "var(--text)",
                      fontFamily: r.mono || r.gold ? "'IBM Plex Mono', monospace" : "inherit",
                      textAlign: "right", wordBreak: "break-all", maxWidth: "60%",
                      letterSpacing: r.gold ? "0.2em" : "normal",
                    }}>
                      {r.value}
                    </span>
                  </div>
                ))}

                <div style={{ marginTop: 24, display: "flex", gap: 12, justifyContent: "space-between" }}>
                  <button className="btn btn-outline btn-lg" onClick={handleBackToEdit}>
                    &larr; Back to Edit
                  </button>
                  <button className="btn btn-gold btn-lg" onClick={handleConfirmSend} disabled={sending}>
                    {sending ? "Dispatching..." : `Confirm & Send ${isoMap[mtType]}`}
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* SWIFT preview on review step too */}
          <div className="card" style={{ alignSelf: "start", position: "sticky", top: 0 }}>
            <div className="card-header">
              <span className="card-title">ISO 20022 XML Message</span>
              <button className="btn btn-outline btn-sm" onClick={() => navigator.clipboard.writeText(preview)}>Copy</button>
            </div>
            <div className="card-body">
              <div className="code-block">{preview}</div>
              <div style={{ marginTop: 12, fontSize: 11, color: "var(--text3)" }}>
                This message will be dispatched to: <strong>{KTT_EMAIL}</strong>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ===== STEP 3: CONFIRMED ===== */}
      {step === 3 && (
        <div className="card" style={{ maxWidth: 600, margin: "0 auto", textAlign: "center" }}>
          <div className="card-body" style={{ padding: 48 }}>
            <div style={{
              width: 72, height: 72, borderRadius: "50%",
              background: "linear-gradient(135deg, var(--teal), var(--teal-light))",
              display: "flex", alignItems: "center", justifyContent: "center",
              margin: "0 auto 24px", fontSize: 32, color: "white",
            }}>
              &#10003;
            </div>
            <h3 style={{ fontFamily: "'Source Serif 4', serif", fontSize: 24, color: "var(--navy)", marginBottom: 8 }}>
              {isoMap[mtType]} ({mtType}) Sent Successfully
            </h3>
            <p style={{ color: "var(--text2)", marginBottom: 24, fontSize: 14 }}>
              Transaction has been recorded and the SWIFT message dispatched via KTT.
            </p>
            <div style={{ display: "flex", gap: 12, justifyContent: "center" }}>
              <button className="btn btn-gold" onClick={handleNewTransaction}>New Transaction</button>
              <button className="btn btn-outline" onClick={() => window.print()}>Print Receipt</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
